import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PayrollEmployeeTerminationCase,
  PayrollEmployeeTerminationImpactPreview,
  PayrollEmployeeTerminationsService,
  PayrollTerminationProcessingReadiness,
} from '../../../services/payroll-employee-terminations/payroll-employee-terminations.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';

export interface TerminationReviewEmployeeIdentity {
  name: string;
  email: string;
}

export interface TerminationReviewDialogData {
  companyId: string;
  case: PayrollEmployeeTerminationCase;
  employeeIdentity: TerminationReviewEmployeeIdentity;
  teamNamesById: Record<string, string>;
  processingEnabled?: boolean;
  deductionCancellationWritesEnabled?: boolean;
  onProcessingUnavailable?: () => void;
}

export type TerminationReviewDialogResult =
  | {action: 'review_removals'; readiness: PayrollTerminationProcessingReadiness}
  | {action: 'completed'; caseId: string}
  | {action: 'resolved'; caseId: string};

@Component({
    selector: 'app-termination-review-dialog',
    templateUrl: './termination-review-dialog.component.html',
    styleUrls: ['./termination-review-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TerminationReviewDialogComponent implements OnInit, OnDestroy {
  public terminationCase: PayrollEmployeeTerminationCase;
  public impactPreview?: PayrollEmployeeTerminationImpactPreview;
  public isLoading = false;
  public loadError = '';
  public isUnavailable = false;
  public processingReadiness?: PayrollTerminationProcessingReadiness;
  public processingLoadError = '';
  public isProcessingUnavailable = false;
  public isCompleting = false;
  public isResolving = false;
  public isResolveConfirmationPending = false;
  public isCancellingDeduction = false;
  public isDeductionCancellationConfirmationPending = false;
  public isDeductionCancellationUncertain = false;
  public deductionCancellationStatus = '';
  private requestVersion = 0;
  private isDialogContextActive = true;

  constructor(
    private terminationService: PayrollEmployeeTerminationsService,
    private dialogRef: MatDialogRef<TerminationReviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TerminationReviewDialogData,
    private uiAlertService?: UiAlertService,
  ) {
    this.terminationCase = data.case;
  }

  ngOnInit(): void {
    void this.loadReview();
  }

  ngOnDestroy(): void {
    this.invalidateDialogContext();
  }

  close(): void {
    this.invalidateDialogContext();
    this.dialogRef.close();
  }

  async loadReview(): Promise<void> {
    if (this.isLoading) {
      return;
    }
    const requestVersion = ++this.requestVersion;
    this.isLoading = true;
    this.loadError = '';
    this.isUnavailable = false;
    this.impactPreview = undefined;
    this.processingReadiness = undefined;
    if (!this.isProcessingUnavailable) {
      this.processingLoadError = '';
    }
    try {
      const [detail, impactPreview] = await Promise.all([
        this.terminationService.getCase(this.data.companyId, this.data.case.id),
        this.terminationService.getImpactPreview(this.data.companyId, this.data.case.id),
      ]);
      if (requestVersion !== this.requestVersion) {
        return;
      }
      this.terminationCase = detail.case;
      this.impactPreview = impactPreview;
      if (this.data.processingEnabled && !this.isProcessingUnavailable) {
        try {
          const readiness = await this.terminationService.getProcessingReadiness(
            this.data.companyId,
            this.data.case.id,
          );
          if (requestVersion !== this.requestVersion || readiness.caseId !== this.data.case.id) {
            return;
          }
          this.processingReadiness = readiness;
        } catch (error: any) {
          if (requestVersion === this.requestVersion) {
            const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
            if (status === 403 || status === 404) {
              this.isLoading = false;
              this.invalidateProcessingContext();
            } else {
              this.processingLoadError = 'Guided termination processing is unavailable right now.';
            }
          }
        }
      }
    } catch (error: any) {
      if (requestVersion !== this.requestVersion) {
        return;
      }
      const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
      this.isUnavailable = status === 403 || status === 404;
      this.loadError = this.isUnavailable
        ? 'This termination review is unavailable for the current company context.'
        : 'Unable to load termination review details right now.';
    } finally {
      if (requestVersion === this.requestVersion) {
        this.isLoading = false;
      }
    }
  }

  async retryReview(): Promise<void> {
    if (this.isLoading || !this.canRetry) {
      return;
    }
    await this.loadReview();
  }

  get canRetry(): boolean {
    return (!!this.loadError && !this.isUnavailable)
      || (!!this.processingLoadError
        && !this.isProcessingUnavailable
        && !this.isDeductionCancellationUncertain);
  }

  get isProcessingAvailable(): boolean {
    return !!this.data.processingEnabled && !this.isProcessingUnavailable;
  }

  get canReviewTeamRemovals(): boolean {
    return this.isProcessingAvailable
      && !!this.processingReadiness?.eligible
      && ['ready_for_removal', 'awaiting_removal', 'awaiting_final_removal']
        .includes(this.processingReadiness?.state || '');
  }

  get canCompleteReview(): boolean {
    return this.isProcessingAvailable
      && this.processingReadiness?.state === 'ready_to_complete'
      && !this.isCompleting;
  }

  get canMarkResolved(): boolean {
    return this.isDialogContextActive
      && this.isProcessingAvailable
      && ['scheduled', 'needs_review', 'blocked'].includes(this.terminationCase.lifecycleStatus)
      && !this.isLoading
      && !this.loadError
      && !this.isCompleting
      && !this.isResolving
      && !this.isResolveConfirmationPending;
  }

  get canCancelPayrollDeduction(): boolean {
    return this.isDialogContextActive
      && this.isProcessingAvailable
      && !!this.data.deductionCancellationWritesEnabled
      && this.processingReadiness?.payrollDeductionCancellation?.state === 'actionable'
      && this.processingReadiness?.payrollDeductionCancellation?.canSubmit === true
      && this.terminationCase.lifecycleStatus === 'needs_review'
      && !this.isLoading
      && !this.loadError
      && !this.isCompleting
      && !this.isResolving
      && !this.isCancellingDeduction
      && !this.isDeductionCancellationConfirmationPending;
  }

  reviewTeamRemovals(): void {
    if (!this.canReviewTeamRemovals || !this.processingReadiness) {
      return;
    }
    this.requestVersion++;
    this.dialogRef.close({action: 'review_removals', readiness: this.processingReadiness});
  }

  async completeReview(): Promise<void> {
    if (!this.canCompleteReview || !this.uiAlertService) {
      return;
    }
    const confirmed = await this.uiAlertService.presentAlertConfirm(
      'Complete this termination review? This records the verified review result and does not perform any member-removal actions.',
    );
    if (!confirmed || !this.canCompleteReview) {
      return;
    }
    const requestVersion = ++this.requestVersion;
    this.isCompleting = true;
    this.processingLoadError = '';
    try {
      const response = await this.terminationService.completeTerminationReview(
        this.data.companyId,
        this.data.case.id,
      );
      if (requestVersion !== this.requestVersion || response.caseId !== this.data.case.id) {
        return;
      }
      this.dialogRef.close({action: 'completed', caseId: response.caseId});
    } catch (error: any) {
      if (requestVersion === this.requestVersion) {
        const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
        if (status === 403 || status === 404) {
          this.invalidateProcessingContext();
        } else {
          this.processingLoadError = 'The termination review could not be completed. Refresh the verified readiness state and try again.';
        }
      }
    } finally {
      if (requestVersion === this.requestVersion) {
        this.isCompleting = false;
      }
    }
  }

  async markResolved(): Promise<void> {
    if (!this.canMarkResolved || !this.uiAlertService) {
      return;
    }
    const confirmationVersion = this.requestVersion;
    const companyId = this.data.companyId;
    const caseId = this.data.case.id;
    this.isResolveConfirmationPending = true;
    let confirmed = false;
    try {
      confirmed = await this.uiAlertService.presentAlertConfirm(
        'Mark this termination review resolved? The case will leave active termination reviews. No membership, allowance, deduction, payroll, or account changes will occur.',
      );
    } finally {
      this.isResolveConfirmationPending = false;
    }
    if (
      !confirmed
      || !this.canMarkResolved
      || !this.isMutationContextCurrent(confirmationVersion, companyId, caseId)
    ) {
      return;
    }
    const requestVersion = ++this.requestVersion;
    this.isResolving = true;
    this.processingLoadError = '';
    try {
      const response = await this.terminationService.resolveTerminationReview(
        companyId,
        caseId,
      );
      if (
        !this.isMutationContextCurrent(requestVersion, companyId, caseId)
        || response.caseId !== caseId
      ) {
        return;
      }
      this.dialogRef.close({action: 'resolved', caseId: response.caseId});
    } catch (error: any) {
      if (requestVersion === this.requestVersion) {
        const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
        if (status === 403 || status === 404) {
          this.invalidateProcessingContext();
        } else {
          this.processingLoadError = 'The termination review could not be marked resolved. No offboarding changes were made; try again.';
        }
      }
    } finally {
      if (requestVersion === this.requestVersion) {
        this.isResolving = false;
      }
    }
  }

  async cancelPayrollDeduction(): Promise<void> {
    if (!this.canCancelPayrollDeduction || !this.uiAlertService) {
      return;
    }
    const confirmationVersion = this.requestVersion;
    const companyId = this.data.companyId;
    const caseId = this.data.case.id;
    this.isDeductionCancellationConfirmationPending = true;
    let confirmed = false;
    try {
      confirmed = await this.uiAlertService.presentAlertConfirm(
        'Cancel this payroll deduction? This submits a UKG Ready deduction cancellation effective today and cancels the associated Wallit Stripe subscription. No refund is issued. Submission means accepted for processing, not independently verified in UKG.',
      );
    } finally {
      this.isDeductionCancellationConfirmationPending = false;
    }
    if (
      !confirmed
      || !this.canCancelPayrollDeduction
      || !this.isMutationContextCurrent(confirmationVersion, companyId, caseId)
    ) {
      return;
    }

    const requestVersion = ++this.requestVersion;
    this.isCancellingDeduction = true;
    this.isDeductionCancellationUncertain = false;
    this.deductionCancellationStatus = '';
    this.processingLoadError = '';
    try {
      const response = await this.terminationService.cancelPayrollDeduction(companyId, caseId);
      if (
        !this.isMutationContextCurrent(requestVersion, companyId, caseId)
        || response.caseId !== caseId
        || response.status !== 'submitted'
      ) {
        return;
      }
      this.deductionCancellationStatus = 'Cancellation submitted';
      this.isCancellingDeduction = false;
      await this.loadReview();
    } catch (error: any) {
      if (requestVersion === this.requestVersion) {
        const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
        if (status === 403 || status === 404) {
          this.invalidateProcessingContext();
        } else {
          this.isDeductionCancellationUncertain = true;
          this.processingReadiness = this.processingReadiness
            ? {
                ...this.processingReadiness,
                payrollDeductionCancellation: {state: 'outcome_unknown', canSubmit: false},
              }
            : undefined;
          this.processingLoadError = 'The cancellation result requires manual review. Do not submit it again.';
        }
      }
    } finally {
      if (requestVersion === this.requestVersion) {
        this.isCancellingDeduction = false;
      }
    }
  }

  private invalidateDialogContext(): void {
    this.isDialogContextActive = false;
    this.requestVersion++;
    this.isResolveConfirmationPending = false;
    this.isDeductionCancellationConfirmationPending = false;
  }

  private isMutationContextCurrent(requestVersion: number, companyId: string, caseId: string): boolean {
    return this.isDialogContextActive
      && requestVersion === this.requestVersion
      && companyId === this.data.companyId
      && caseId === this.data.case.id
      && caseId === this.terminationCase.id;
  }

  private invalidateProcessingContext(): void {
    this.requestVersion++;
    this.processingReadiness = undefined;
    this.processingLoadError = 'Guided termination processing is unavailable for this review.';
    this.isProcessingUnavailable = true;
    this.isCompleting = false;
    this.isResolving = false;
    this.isResolveConfirmationPending = false;
    this.isCancellingDeduction = false;
    this.isDeductionCancellationConfirmationPending = false;
    this.isDeductionCancellationUncertain = false;
    this.data.deductionCancellationWritesEnabled = false;
    this.data.onProcessingUnavailable?.();
  }

  get reviewLabel(): string {
    if (this.terminationCase.lifecycleStatus === 'completed') {
      return 'Completed';
    }
    if (this.terminationCase.lifecycleStatus === 'dismissed') {
      if (this.terminationCase.dismissalReason === 'initial_baseline_history') {
        return 'Baseline history';
      }
      if (this.terminationCase.dismissalReason === 'admin_marked_resolved') {
        return 'Marked resolved';
      }
      return 'Resolved';
    }
    if (this.terminationCase.lifecycleStatus === 'scheduled') {
      return 'Termination scheduled';
    }
    if (
      this.terminationCase.lifecycleStatus === 'needs_review'
      && this.terminationCase.reviewEligible
      && this.terminationCase.actionable
    ) {
      return 'Termination review';
    }
    return 'Manual review';
  }

  get requiresManualReview(): boolean {
    return this.reviewLabel === 'Manual review'
      || !!this.impactPreview?.pendingSettlement?.requiresManualReview
      || (!!this.impactPreview?.payrollDeductions?.requiresManualReview
        && this.processingReadiness?.payrollDeductionCancellation?.state !== 'submitted');
  }

  teamName(teamId: string | null | undefined): string {
    return (teamId && this.data.teamNamesById[teamId]) || 'Company team';
  }

  evidenceRecordLabel(recordType: string): string {
    const normalized = `${recordType || ''}`.trim().toLowerCase();
    if (normalized.includes('direct')) {
      return 'Direct enrollment evidence';
    }
    if (normalized.includes('import')) {
      return 'Payroll deduction import evidence';
    }
    return 'Payroll deduction evidence';
  }

  identityEvidenceLabel(identityEvidence: string): string {
    return identityEvidence === 'exact_provider_identity'
      || identityEvidence === 'exact_company_employee_identity'
      ? 'Exact stored identity evidence'
      : 'Identity evidence requires manual review';
  }

  statusLabel(value: string | null | undefined): string {
    const words = `${value || ''}`.trim().toLowerCase().split('_').filter((word) => !!word);
    if (!words.length) {
      return 'Not available';
    }
    const label = words.join(' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
}
